/**
 * @file: usePreventOrderNavigateAway.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { AsideContext } from '@euler/components/Aside';
import { NavigationAction } from '@react-navigation/native';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useOrderNavigateAwayConfirm } from './useOrderNavigateAwayConfirm';

export function usePreventOrderNavigateAway() {
  const {
    mode,
    navigation,
    dirty$,
    skipDirtyCheck,
    draftId,
    draftEnabled,
    isFinished,
  } = useOrderFlowContext();

  const { draftManager } = useOrderContext();

  const draftIdRef = useRef(draftId);
  draftIdRef.current = draftId;

  const aside = useContext(AsideContext);
  const navigationAction = useRef<NavigationAction>();

  const onConfirm = useCallback(() => {
    if (!navigationAction.current) return;
    if (aside) {
      aside?.dismiss(navigationAction.current);
    } else {
      navigation.dispatch(navigationAction.current);
    }
    navigationAction.current = undefined;
  }, [aside, navigation]);

  const confirmLeave = useOrderNavigateAwayConfirm(onConfirm);

  useEffect(() => {
    return navigation.addListener('beforeRemove', e => {
      if (isFinished.current) {
        return;
      }

      const draft = draftManager.getDraft(draftIdRef.current);
      if (
        skipDirtyCheck.current ||
        !dirty$.value ||
        (mode === 'new' && !draft)
      ) {
        // delegate to aside to dismiss and go back
        if (aside != null) {
          e.preventDefault();
          aside.dismiss(e.data.action);
        }
        return;
      }

      e.preventDefault();

      if (mode === 'new') {
        draftEnabled.current = false;
      }

      navigationAction.current = e.data.action;

      confirmLeave();
    });
  }, [
    aside,
    confirmLeave,
    dirty$,
    draftEnabled,
    draftManager,
    isFinished,
    mode,
    navigation,
    skipDirtyCheck,
  ]);
}
